import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../shared/authService';
import { InlineLoader } from '../../components/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import './Login.css';

function Login() {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Safely handle the redirect URL - ensure it's a relative path for security
  const getRedirectPath = () => {
    const defaultPath = '/';
    const fromPath = location.state?.from;

    // Only accept relative paths that start with / and don't contain protocol indicators
    if (
      fromPath &&
      typeof fromPath === 'string' &&
      fromPath.startsWith('/') &&
      !fromPath.includes('://') &&
      !fromPath.includes('//')
    ) {
      return fromPath;
    }

    return defaultPath;
  };

  const from = getRedirectPath();

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          // Use a setTimeout to avoid immediate navigation which can cause security errors
          setTimeout(() => {
            navigate(from, { replace: true });
          }, 10);
        }
      } catch (err) {
        console.error('Auth check error:', err);
        // Clear any invalid auth data
        authService.clearSession();
      }
    };

    checkAuth();
  }, [navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username || !password) {
      setError(t('auth.enterCredentials'));
      return;
    }

    try {
      setError('');
      setLoading(true);

      await authService.login(username, password);

      // Use window.location for safer navigation after login
      window.location.href = from;
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page page-center">
      <div className="container container-tight py-4">
        <div className="text-center mb-4">
          <a href="/" className="navbar-brand navbar-brand-autodark">
            <img src="/static/img/logo.svg" alt="Logo" height="36" />
          </a>
        </div>

        <div className="card card-md">
          <div className="card-body">
            <h2 className="h2 text-center mb-4">{t('auth.loginTitle')}</h2>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} autoComplete="off" noValidate>
              <div className="mb-3">
                <label className="form-label">{t('auth.username')}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('auth.usernamePlaceholder')}
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  {t('auth.password')}
                  <span className="form-label-description">
                    <a href="/accounts/password_reset/">{t('auth.forgotPassword')}</a>
                  </span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-control"
                    placeholder={t('auth.passwordPlaceholder')}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ paddingRight: '2.5rem' }}
                    required
                  />
                  <button
                    type="button"
                    className="btn btn-link p-0 link-secondary"
                    onClick={() => setShowPassword(prev => !prev)}
                    title={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                    style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', lineHeight: 1 }}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icon-tabler-eye-off">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                        <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828" />
                        <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87" />
                        <path d="M3 3l18 18" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icon-tabler-eye">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                        <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                        <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="mb-2">
                <label className="form-check">
                  <input type="checkbox" className="form-check-input" />
                  <span className="form-check-label">
                    {t('auth.rememberMe')}
                  </span>
                </label>
              </div>

              <div className="form-footer">
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <InlineLoader className="me-2" />
                      {t('auth.signingIn')}
                    </>
                  ) : (
                    t('auth.signIn')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="text-center text-muted mt-3">
          {t('auth.noAccount')}{' '}
          <a href="/accounts/register/" tabIndex="-1">
            {t('auth.signUp')}
          </a>
        </div>
      </div>
    </div>
  );
}

export default Login;
